import { useCreateOrUpdateLocationGroup, useFetchLocationGroupDetail } from 'api/locations/hooks';
import { Button, Page, RamblaActionButtonContent, Wrapper } from 'components';
import ConditionalSpinner from 'components/ConditionalSpinner/ConditionalSpinner';
import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import Skeleton from 'react-loading-skeleton';
import { useAuthStore } from 'stores/authStore';
import { LOCATION_GROUPS, RETAILER_LOCATION_GROUPS } from 'constants/routes';
import { toastr } from 'utils';
import { uploadMedia } from 'utils/mediaResourceUtils';
import EditLocationGroupForm from '../../containers/forms/editLocationGroupForm';
const EditLocationGroupPage = () => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'locationGroups' });
  const { t: tApp } = useTranslation('app');
  const { id } = useParams();
  const isNewLocationGroup = id === 'new';
  const role = useAuthStore((state) => state.role);

  const { data: locationGroup, isLoading } = useFetchLocationGroupDetail(
    isNewLocationGroup ? null : id,
  );

  const { mutateAsync: createOrUpdateLocationGroup } = useCreateOrUpdateLocationGroup();

  const navigate = useNavigate();

  const initialValues = useMemo(
    () =>
      isNewLocationGroup
        ? {
            name: '',
            vanityUrl: '',
            logoId: [],
            logo: [],
            coverImageId: [],
            coverImage: [],
            locations: [],
          }
        : {
            name: locationGroup?.name || '',
            vanityUrl: locationGroup?.vanity_url || '',
            logoId: locationGroup?.logo_id ? [locationGroup.logo_id] : [],
            logo: locationGroup?.logo ? [locationGroup.logo] : [],
            coverImageId: locationGroup?.cover_image_id ? [locationGroup.cover_image_id] : [],
            coverImage: locationGroup?.cover_image ? [locationGroup.cover_image] : [],
            locations: locationGroup?.locations?.map((l) => l.uid) || [],
          },
    [isNewLocationGroup, locationGroup],
  );

  const [formValues, setFormValues] = useState(initialValues);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vanityAvailability, setVanityAvailability] = useState({ shouldBlockSubmit: false });

  const fetched = isNewLocationGroup ? true : !isLoading;
  const locationGroupsRoute = role === 'ADMIN' ? LOCATION_GROUPS : RETAILER_LOCATION_GROUPS;
  const currentBreadcrumbLabel = isNewLocationGroup ? (
    t('createModal.title')
  ) : isLoading ? (
    <Skeleton width={140} />
  ) : (
    locationGroup?.name || tApp('breadcrumbs.locationGroup')
  );

  const hasChanges = useMemo(() => {
    if (isNewLocationGroup) {
      return (
        formValues.name?.trim() !== '' ||
        formValues.vanityUrl?.trim() !== '' ||
        formValues.logoId?.length > 0 ||
        formValues.coverImageId?.length > 0 ||
        formValues.locations?.length > 0
      );
    }

    return isFormDirty;
  }, [isFormDirty, isNewLocationGroup, formValues]);

  const handleSave = async () => {
    if (!hasChanges || vanityAvailability.shouldBlockSubmit) return;

    setIsSubmitting(true);

    try {
      const values = formValues;
      let publicLogoId = '';
      let publicCoverImageId = '';

      const logoItems = values.logoId || [];
      const coverImageItems = values.coverImageId || [];
      const [logoItem] = logoItems;
      const [coverImageItem] = coverImageItems;

      if (logoItem instanceof File) {
        const { public_id } = await uploadMedia([logoItem]);
        publicLogoId = public_id;
      } else if (logoItem) {
        publicLogoId = logoItem;
      }

      if (coverImageItem instanceof File) {
        const { public_id } = await uploadMedia([coverImageItem]);
        publicCoverImageId = public_id;
      } else if (coverImageItem) {
        publicCoverImageId = coverImageItem;
      }

      const payload = {
        name: values.name,
        vanity_url: values.vanityUrl,
        location_uids: values.locations || [],
        logo_id: publicLogoId || null,
        cover_image_id: publicCoverImageId || null,
      };

      if (!isNewLocationGroup) {
        payload.uid = locationGroup.uid;
      }

      await createOrUpdateLocationGroup(payload);
      toastr.success(
        t('toastr:success'),
        isNewLocationGroup ? t('createModal.successMessage') : t('editModal.successMessage'),
      );

      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/location-groups');
      }
    } catch (error) {
      toastr.error(
        t('toastr:error'),
        isNewLocationGroup ? t('createModal.errorMessage') : t('editModal.errorMessage'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page
      title={isNewLocationGroup ? t('createModal.title') : t('editModal.title')}
      breadcrumbs={[
        { label: tApp('breadcrumbs.locationGroups'), to: locationGroupsRoute },
        { label: currentBreadcrumbLabel },
      ]}
      titleSeparator
      style={{ background: 'white' }}
      loading={!fetched}
      withBackButton={false}
      footer={{
        component: (
          <div style={{ marginLeft: 'auto' }}>
            <Button
              size="lg"
              variant="dark"
              className="rounded"
              disabled={!hasChanges || isSubmitting || vanityAvailability.shouldBlockSubmit}
              onClick={handleSave}
            >
              <ConditionalSpinner showSpinner={isSubmitting}>
                {isNewLocationGroup ? (
                  <RamblaActionButtonContent icon="addModule">
                    {t('createModal.submit')}
                  </RamblaActionButtonContent>
                ) : (
                  t('editModal.submit')
                )}
              </ConditionalSpinner>
            </Button>
          </div>
        ),
        visible: true,
      }}
    >
      <Helmet>
        <title>{isNewLocationGroup ? t('createModal.title') : t('editModal.title')}</title>
      </Helmet>
      <Wrapper style={{ marginBottom: '96px', marginTop: '10px' }}>
        <EditLocationGroupForm
          initialValues={initialValues}
          setIsFormDirty={setIsFormDirty}
          setFormValues={setFormValues}
          setVanityAvailability={setVanityAvailability}
        />
      </Wrapper>
    </Page>
  );
};

export default EditLocationGroupPage;
